import { ErrorBoundary } from '@sentry/react';
import { ButtonIcon, Loader } from '@statsbomb/kitbag-components';
import { useSetAtom } from 'jotai';
import { ReactNode, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { playerIdAtom } from '@/atoms/player/player';
import { CloseCardHeaderButton } from '@/components/button/CloseCardHeaderButton';
import { SavePlayersToShortlistsWithNavigate } from '@/components/shortlists/SavePlayersToShortlistsWithNavigate';
import { METRIC_KEY, PLAYER_ID } from '@/consts/searchKeys';
import { PlayerPreviewInfo } from './PlayerPreviewInfo';
import { PlayerPreviewTitle } from './PlayerPreviewTitle';

// The dashboard URL is resolved by the consumer so this panel stays ignorant of classic vs
// new player experience. Rendered only when there is a destination — the button carries
// `as="span"` here to avoid an invalid <a><button></a> nesting.
const PlayerDashboardLink = ({ dashboardHref, children }: { dashboardHref: string; children: ReactNode }) => (
  <a href={dashboardHref} className="self-start" target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

// `as` defaults to a real <button> so the disabled/pending states are genuinely announced to
// assistive tech (a <span disabled> is not). Callers wrapping this in an anchor pass `as="span"`.
const ViewDashboardButton = ({
  isLoading,
  isDisabled,
  as = 'button',
}: {
  isLoading?: boolean;
  isDisabled?: boolean;
  as?: 'button' | 'span';
}) => {
  const { t } = useTranslation('preview');

  return (
    <ButtonIcon
      icon="Start"
      displayText="right"
      size="small"
      variant="secondary"
      disabled={isLoading || isDisabled}
      isPending={isLoading}
      as={as}
    >
      {t('viewDashboard')}
    </ButtonIcon>
  );
};

export const PlayerPreviewHeader = ({ dashboardHref }: { dashboardHref?: string }) => {
  const setPlayerId = useSetAtom(playerIdAtom);

  const [searchParams] = useSearchParams();
  const playerId = searchParams.get(PLAYER_ID);

  return (
    <header className="flex flex-col p-4 border-b border-canvas-secondary-main">
      <div className="flex justify-between items-center">
        <Suspense fallback={<Loader size="1rem" />}>
          <PlayerPreviewTitle />
        </Suspense>
        <CloseCardHeaderButton
          navigationPath=".."
          paramsToDelete={[METRIC_KEY, `playerId`]}
          onClick={() => setPlayerId(null)}
        />
      </div>
      <div className="pb-4">
        <Suspense fallback={<Loader size="1rem" />}>
          <PlayerPreviewInfo />
        </Suspense>
      </div>
      <ErrorBoundary fallback={<ViewDashboardButton isDisabled />}>
        <Suspense fallback={<ViewDashboardButton isLoading />}>
          <div className="flex gap-x-2">
            {dashboardHref ? (
              <PlayerDashboardLink dashboardHref={dashboardHref}>
                <ViewDashboardButton as="span" />
              </PlayerDashboardLink>
            ) : (
              <ViewDashboardButton isDisabled />
            )}

            {playerId && <SavePlayersToShortlistsWithNavigate playerIds={[playerId]} />}
          </div>
        </Suspense>
      </ErrorBoundary>
    </header>
  );
};
